import { openDB } from 'idb';
import { DATA_VERSION } from './version';

// Max time to wait for IndexedDB to open before falling back to the network.
// A healthy local open is single-digit ms; a slow one means a blocked/locked
// DB, so fail fast and hit the network instead of stalling the UI.
const DB_OPEN_TIMEOUT_MS = 1000;

export class DataManager {
  constructor(accountId) {
    this.modelsToSync = ['inbox', 'label', 'team'];
    this.accountId = accountId;
    this.db = null;
  }

  async initDb() {
    if (this.db) return this.db;
    const dbName = `cw-store-${this.accountId}`;

    // A locked/blocked IndexedDB (e.g. a stale Chrome handle held by another
    // tab/process) makes `openDB` hang forever instead of throwing. Race it
    // against a timeout so callers can fall back to the network rather than
    // wedging the UI on stale/empty cache.
    const open = openDB(`cw-store-${this.accountId}`, DATA_VERSION, {
      upgrade(db) {
        if (db.objectStoreNames.contains('cache-keys')) {
          db.deleteObjectStore('cache-keys');
        }
        db.createObjectStore('cache-keys');
        if (db.objectStoreNames.contains('inbox')) {
          db.deleteObjectStore('inbox');
        }
        db.createObjectStore('inbox', { keyPath: 'id' });
        if (db.objectStoreNames.contains('label')) {
          db.deleteObjectStore('label');
        }
        db.createObjectStore('label', { keyPath: 'id' });
        if (db.objectStoreNames.contains('team')) {
          db.deleteObjectStore('team');
        }
        db.createObjectStore('team', { keyPath: 'id' });
      },
    });

    let timer;
    const timeout = new Promise((_, reject) => {
      timer = setTimeout(
        () => reject(new Error(`IndexedDB ${dbName} open timed out`)),
        DB_OPEN_TIMEOUT_MS
      );
    });

    try {
      this.db = await Promise.race([open, timeout]);
    } finally {
      clearTimeout(timer);
    }

    // Store the database name in LocalStorage
    const dbNames = JSON.parse(localStorage.getItem('cw-idb-names') || '[]');
    if (!dbNames.includes(dbName)) {
      dbNames.push(dbName);
      localStorage.setItem('cw-idb-names', JSON.stringify(dbNames));
    }

    return this.db;
  }

  validateModel(name) {
    if (!name) throw new Error('Model name is not defined');
    if (!this.modelsToSync.includes(name)) {
      throw new Error(`Model ${name} is not defined`);
    }
    return true;
  }

  async replace({ modelName, data }) {
    this.validateModel(modelName);

    this.db.clear(modelName);
    return this.push({ modelName, data });
  }

  async push({ modelName, data }) {
    this.validateModel(modelName);

    if (Array.isArray(data)) {
      const tx = this.db.transaction(modelName, 'readwrite');
      data.forEach(item => {
        tx.store.add(item);
      });
      await tx.done;
    } else {
      await this.db.add(modelName, data);
    }
  }

  async get({ modelName }) {
    this.validateModel(modelName);
    return this.db.getAll(modelName);
  }

  async setCacheKeys(cacheKeys) {
    Object.keys(cacheKeys).forEach(async modelName => {
      this.db.put('cache-keys', cacheKeys[modelName], modelName);
    });
  }

  async getCacheKey(modelName) {
    this.validateModel(modelName);

    return this.db.get('cache-keys', modelName);
  }
}
